import { WorkItemCreateLayout, WorkItemLayout, WorkItemPermissionsProvider } from "components/work-items";
import { WorkItemsGrid } from "components/work-items/grid";
import { WorkItemNotes } from "components/work-items/notes";
import { useWorkItemPermissions } from "hooks/work-items";
import { Route, Routes, Navigate, Outlet } from "react-router-dom";
import { ErrorHandling, RequirePermissionProps, useSnackbar } from "@sagecentral/core-ui";
import CustomCircularProgress from "components/shared/layout";
import { Box, CircularProgress, styled } from '@mui/material';
import WorkItemOverview from 'components/work-items/overview';
import { AuditScope } from "components/shared/audit";
import { ErrorHandlerProvider } from "hooks/shared";
import WorkItemMetadata from "components/work-items/metadata/WorkItemMetadata";
import { useFeatureEnabled } from "hooks/shared";
import { Workflows } from "components/work-items/workflow";
import { WorkItemMyTasks } from "components/work-items/tasks";
import { WorkItemProductMaster } from "components/work-items/product-master";
import { WorkItemAttachmentsGrid } from "components/work-items/attachments";
import RelatedWorkItemsGridPage from "components/work-items/related";
import { ResourceAllocation } from "components/work-items/resource-allocation";
import { WorkItemCreateBasicInfo } from "components/work-items/create";

const FormTitle = styled(Box)({
    fontSize: '22px',
    marginBottom: '10px',
    fontWeight: 'bold'
});

interface RequireProductMasterPermissionsProps {
    children: React.ReactNode;
}

const RequireWorkItemsPermission = (props: RequirePermissionProps) => {
    const permissions = useWorkItemPermissions() as any;
    if (!permissions) return <CircularProgress />;
    const authorized = (permissions[props.permission]) || false;
    return authorized ? <Outlet /> : <ErrorHandling error={{ message: "Unauthorized ", name: "Error", cause: 403 }} />;
};

const RequirePermission = (props: RequirePermissionProps) => {
    const permissions = useWorkItemPermissions() as any;
    if (permissions == null) return <CircularProgress />;
    const authorized = (permissions[props.permission]) || false;
    return authorized ? <>{props.children}</> : <ErrorHandling error={{ message: "Unauthorized ", name: "Error", cause: 403 }} />;
};

const RequireProductMasterPermissions = (props: RequireProductMasterPermissionsProps) => {

    const permissions = useWorkItemPermissions() as any;
    if (permissions == null) return <CircularProgress />;
    const authorized = (permissions['canLinkProductMaster'] || permissions['canUnlinkProductMaster'] ) || false;
    return authorized ? <>{props.children}</> : <ErrorHandling error={{ message: "Unauthorized ", name: "Error", cause: 403 }} />;
};


const WorkItemRoutes = () => {
    const { displaySnackbar } = useSnackbar();
    const handler = async (resp: Response, error?: any) => {

        if (resp.status === 400) {
            const error = await resp.json();
            displaySnackbar('warning', error.errors['*'] ?? error.title ?? 'Field update failed');

            return true;
        }

        if (resp.status !== 403) return false;
        const message = await resp.text();
        displaySnackbar('error', message);
        return true;
    };
    const [wave4Enabled] = useFeatureEnabled('wave-4');

    const wave4Routes = (
        <Route path=":workItemId" element={<WorkItemLayout />}>
            <Route index element={<Navigate to='overview' replace />} />
            <Route path="overview" element={<AuditScope><WorkItemOverview /></AuditScope>} />
            <Route path="metadata" element={<AuditScope><WorkItemMetadata /></AuditScope>} />
            <Route path="notes" element={<AuditScope><WorkItemNotes /></AuditScope>} />
            <Route path="tasks" element={<AuditScope><WorkItemMyTasks /></AuditScope>} />
            <Route path="workflows" element={
                <RequirePermission permission="canViewWorkflows">
                    <AuditScope><Workflows /></AuditScope>
                </RequirePermission>} />
            <Route path="product-master" element={<AuditScope><WorkItemProductMaster /></AuditScope>} />
            <Route path="attachments" element={<WorkItemAttachmentsGrid />} />
            <Route path="metadata" element={<AuditScope><WorkItemMetadata /></AuditScope>} />
            <Route path="related" element={<AuditScope><RelatedWorkItemsGridPage /></AuditScope>} />
            <Route path="resources" element={
                <AuditScope><ResourceAllocation /></AuditScope>
            } />
            <Route path="*" element={<ErrorHandling error={{ message: "Base error", name: "Error", cause: 404 }} />} />
        </Route>
    );

    const wave4OffRoutes = (
        <Route path=":workItemId" element={<WorkItemLayout hideTabs />}>
            <Route path="notes" element={<AuditScope><WorkItemNotes /></AuditScope>} />

            <Route path="*" element={<ErrorHandling error={{ message: "Base error", name: "Error", cause: 404 }} />} />
        </Route>
    );

    return (
        <WorkItemPermissionsProvider>
            <ErrorHandlerProvider value={handler}>
                <Routes>
                    <Route path='my/*' element={
                        <Outlet />
                    }>
                        <Route index element={<Navigate to="in-progress" replace={true} />} />
                        <Route path="in-progress" element={<WorkItemsGrid Status="in-progress" all={false} />} />
                        <Route path="inprogress" element={<Navigate to='in-progress' replace />} />
                        <Route path="upcoming" element={<WorkItemsGrid Status="upcoming" all={false} />} />
                        <Route path="cancelled" element={<WorkItemsGrid Status="cancelled" all={false} />} />
                        <Route path="onhold" element={<Navigate to='on-hold' replace />} />
                        <Route path="on-hold" element={<WorkItemsGrid Status="on-hold" all={false} />} />
                        <Route path="completed" element={<WorkItemsGrid Status="completed" all={false} />} />
                        <Route path="all" element={<WorkItemsGrid Status="all" all={false} />} />
                        <Route path="*" element={<ErrorHandling error={{ message: "Base error", name: "Error", cause: 404 }} />} />
                    </Route>
                    {wave4Enabled ? wave4Routes : wave4OffRoutes}
                    <Route path='all/*' element={
                        <RequireWorkItemsPermission permission="canAccessAllWorkItems">
                            <Outlet />
                        </RequireWorkItemsPermission>}
                    >
                        <Route index element={<WorkItemsGrid Status="all" all />} />
                        <Route path="in-progress" element={<WorkItemsGrid Status="in-progress" all />} />
                        <Route path="inprogress" element={<Navigate to='in-progress' replace />} />
                        <Route path="upcoming" element={<WorkItemsGrid Status="upcoming" all />} />
                        <Route path="cancelled" element={<WorkItemsGrid Status="cancelled" all />} />
                        <Route path="onhold" element={<Navigate to='on-hold' replace />} />
                        <Route path="on-hold" element={<WorkItemsGrid Status="on-hold" all />} />
                        <Route path="completed" element={<WorkItemsGrid Status="completed" all />} />
                        <Route path="*" element={<ErrorHandling error={{ message: "Base error", name: "Error", cause: 404 }} />} />
                    </Route>

                    <Route path="create" element={
                        <RequirePermission permission="canCreateWorkItem">
                            <AuditScope><WorkItemCreateLayout /></AuditScope>
                        </RequirePermission>}>
                        <Route index element={<Navigate to='basic-info' replace />} />
                        <Route path="basic-info" element={<WorkItemCreateBasicInfo />} />
                    </Route>
                    <Route path="create/:draftId" element={
                        <RequirePermission permission="canCreateWorkItem">
                            <AuditScope><WorkItemCreateLayout /></AuditScope>
                        </RequirePermission>}>
                        <Route index element={<Navigate to="basic-info" replace />} />
                        <Route path="basic-info" element={<WorkItemCreateBasicInfo />} />
                        <Route path="resources" element={<AuditScope>
                            <ResourceAllocation draft={true} />
                        </AuditScope>} />
                        <Route path="related" element={<AuditScope><RelatedWorkItemsGridPage draft={true} /></AuditScope>} />
                        <Route path="product-master" element={<RequireProductMasterPermissions><AuditScope><WorkItemProductMaster draft /></AuditScope></RequireProductMasterPermissions>} />
                        <Route path="metadata" element={<AuditScope>
                            <WorkItemMetadata draft />
                        </AuditScope>} />
                        <Route path="attachments" element={<WorkItemAttachmentsGrid draft />} />
                        <Route path="notes" element={<WorkItemNotes draft />} />
                    </Route>
                </Routes>
            </ErrorHandlerProvider>
        </WorkItemPermissionsProvider>
    );
};

export default WorkItemRoutes;